import { supabase } from "./supabase";

export type ReflectionGoal = "1-2" | "3-5" | "6+";

export interface ReflectionProgress {
	currentCount: number;
	goalMin: number;
	goalMax: number;
	percentage: number;
	status: "behind" | "on-target" | "smashing-it";
	weeksTracked: number;
	weeklyAverage: number;
}

/**
 * Calculate the min and max for a given goal
 */
function getGoalRange(goal: ReflectionGoal): { min: number; max: number } {
	switch (goal) {
		case "1-2":
			return { min: 1, max: 2 };
		case "3-5":
			return { min: 3, max: 5 };
		case "6+":
			return { min: 6, max: 7 }; // Use 7 as the "max" for display purposes
	}
}

/**
 * Calculate reflection progress based on the last 4 weeks of check-ins
 */
export async function calculateReflectionProgress(
	userId: string,
	goal: ReflectionGoal | null | undefined,
): Promise<ReflectionProgress | null> {
	if (!goal) {
		return null;
	}

	try {
		// Calculate date 7 days ago
		const sevenDaysAgo = new Date();
		sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

		// Fetch check-ins from the last 4 weeks
		const { data: checkIns, error } = await supabase
			.from("daily_checkins")
			.select("id, created_at")
			.eq("user_id", userId)
			.gte("created_at", sevenDaysAgo.toISOString())
			.order("created_at", { ascending: true });

		if (error) throw error;

		const currentCount = checkIns?.length || 0;
		const { min, max } = getGoalRange(goal);

		// For last 7 days only
		const weeksTracked = 1;
		const weeklyAverage = currentCount; // count over the last 7 days

		// Weekly targets (per 7 days)
		const adjustedMin = min;
		const adjustedMax = max;

		// Percentage relative to weekly max (cap at 150%)
		const percentage =
			adjustedMax > 0 ? Math.min(150, (weeklyAverage / adjustedMax) * 100) : 0;

		// Determine status based on weekly average
		let status: "behind" | "on-target" | "smashing-it";
		if (weeklyAverage >= 7) {
			status = "smashing-it";
		} else if (weeklyAverage < min) {
			status = "behind";
		} else if (weeklyAverage > max) {
			status = "smashing-it";
		} else {
			status = "on-target";
		}

		return {
			currentCount,
			goalMin: adjustedMin,
			goalMax: adjustedMax,
			percentage,
			status,
			weeksTracked,
			weeklyAverage,
		};
	} catch (error) {
		console.error("Error calculating reflection progress:", error);
		return null;
	}
}

/**
 * Set the user's reflection goal
 */
export async function setReflectionGoal(
	userId: string,
	goal: ReflectionGoal,
): Promise<{ error: any }> {
	try {
		const { error } = await supabase
			.from("profiles")
			.update({
				reflection_goal: goal,
				reflection_goal_set_at: new Date().toISOString(),
			})
			.eq("id", userId);

		return { error };
	} catch (error) {
		return { error };
	}
}
